import { type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BsPlus } from 'react-icons/bs';
import { Button } from 'react-bootstrap';

import { addElementToPage, editElementsCollapseState } from '@editor2/reducers/editor/editorSlice';
import { type RootState } from '@editor2/reducers/root';
import { addElementsBlock } from '@editor2/containers/PageElementsPage/styles';
import { getNewElement, isDoubleElement } from '@editor2/services/elementService';
import { ElementType } from '@editor2/types/pageElements';
import { DEPRECATED_ELEMENTS } from '@editor2/types/deprecatedElements';
import { ToastSeverity } from '@editor2/types/toastSeverity';
import { showToast } from '@editor2/reducers/toastSlice';
import { CollapseState } from '@editor2/types/collapseState';
import { getEnabledPageElementsTypes } from '@editor2/services/pagesService';
import { useAiModels } from '@editor2/hooks/useAiModels';
import AiModelsStatus from '@editor2/components/AiModelsStatus';

interface Props {
	courseId: number;
	pageId: string;
}

const AddElementsBlock: FC<Props> = (props) => {
	const { courseId, pageId } = props;

	const dispatch = useDispatch();

	const showInfoToast = (message: string) => {
		dispatch(
			showToast({
				severity: ToastSeverity.Info,
				message
			})
		);
	};

	const { pages } = useSelector((state: RootState) => state.courses[courseId]);
	const { page_types, element_types } = useSelector(
		(state: RootState) => state.courses[courseId].config
	);

	const pageType = pages.entities[pageId].page_type;
	const elementTypes = getEnabledPageElementsTypes(page_types, pageType);
	const elementTypesKeys = Object.keys(elementTypes);
	const isChatbox = (type: string) => (element_types[type]?.type || type) === 'AiChatbox';
	const offersChatbox = elementTypesKeys.some(isChatbox);
	const catalog = useAiModels(offersChatbox);
	const hasDefaults = catalog.models?.some((model) => model.default);
	const chatboxBlocked = !hasDefaults || catalog.error || catalog.loading;

	const onAddElement = (elementType: string) => {
		if (isChatbox(elementType) && chatboxBlocked) return;
		const elementConfig = element_types[elementType];
		const elementEntity = getNewElement(elementType, elementConfig, catalog.models);

		dispatch(addElementToPage({ pageId, elementEntity, courseId }));
		dispatch(editElementsCollapseState({ collapseState: CollapseState.Mixed, courseId }));

		if (isDoubleElement(ElementType[elementType])) {
			const elementEntity = getNewElement(elementType, elementConfig);
			const payload = { pageId, elementEntity: { ...elementEntity, direction: 'out' }, courseId };

			dispatch(addElementToPage(payload));
		}

		showInfoToast(`${elementTypes[elementType].human_name} was added to the bottom.`);
	};

	return (
		<div className={addElementsBlock}>
			<div className="title">Append: </div>
			{offersChatbox && (
				<AiModelsStatus {...catalog} noDefaults={!!catalog.models && !hasDefaults} />
			)}
			<div className="elements">
				{elementTypesKeys.map(
					(type) =>
						!DEPRECATED_ELEMENTS.includes(ElementType[type]) && (
							<Button
								variant="outline-secondary"
								onClick={() => onAddElement(type)}
								key={type}
								disabled={isChatbox(type) && chatboxBlocked}
							>
								<BsPlus />
								{elementTypes[type].human_name}
							</Button>
						)
				)}
			</div>
		</div>
	);
};

export default AddElementsBlock;
